// client/src/components/documentos/AnamnesesHistoricoPage.js
import React, { Fragment, useState, useCallback } from 'react';
import { ClipboardList, Eye, Loader2, X, PlusCircle, ArrowLeft, PenLine, Link2, FileEdit, Trash2 } from 'lucide-react';
import PageHeader from '../layout/PageHeader';
import FabButton from '../common/FabButton';
import ActionsDrawer from '../common/ActionsDrawer';
import AnamneseForm from '../anamnese/AnamneseForm';
import AssinarAnamneseModal from '../anamnese/AssinarAnamneseModal';
import GerarLinkAnamneseModal from '../anamnese/GerarLinkAnamneseModal';
import { useAnamnesesHistorico, useAnamneseHistoricoById } from '../../hooks/useAnamnesesHistorico';
import { useDeleteAnamnese } from '../../hooks/useAnamneses';

const fmtData = (data) => (data ? new Date(data).toLocaleString('pt-BR') : '—');

// Status de workflow (server/models/Anamnese.js:status) — ortogonal à coluna
// "Status" existente (validade/expirada), que se refere ao dataValidade.
const STATUS_WORKFLOW_LABEL = {
    draft: { label: 'Rascunho', className: 'badge-gray' },
    filled: { label: 'Preenchida', className: 'badge-blue' },
    awaiting_signature: { label: 'Aguardando Assinatura', className: 'badge-yellow' },
    signed: { label: 'Assinada', className: 'badge-green' },
};

const getNome = (entidade, campo = 'nome') => {
    if (!entidade || typeof entidade !== 'object') return '—';
    if (campo === 'nome') return entidade.user?.name || entidade.nome || '—';
    return entidade[campo] || '—';
};

const formatValorResposta = (valor) => {
    if (Array.isArray(valor)) return valor.length > 0 ? valor.join(', ') : '—';
    if (typeof valor === 'boolean') return valor ? 'Sim' : 'Não';
    return valor || valor === 0 ? String(valor) : '—';
};

const AnamnesePreviewModal = ({ anamneseId, onClose }) => {
    const { data: ficha, isLoading } = useAnamneseHistoricoById(anamneseId);
    const clienteNome = getNome(ficha?.cliente);
    const servicoNome = ficha?.servico && typeof ficha.servico === 'object' ? ficha.servico.nome : null;
    const preenchidoPorNome = ficha?.preenchidoPor && typeof ficha.preenchidoPor === 'object' ? ficha.preenchidoPor.name : null;

    return (
        <div
            style={{
                position: 'fixed', inset: 0, zIndex: 1000,
                background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: '1rem',
            }}
            onClick={onClose}
        >
            <div
                style={{
                    background: '#fff', borderRadius: 12, padding: '1.5rem', maxWidth: 680, width: '100%',
                    maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
                }}
                onClick={(e) => e.stopPropagation()}
            >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                    <h3 className="form-title" style={{ margin: 0 }}>{ficha?.templateNome || 'Anamnese'}</h3>
                    <button onClick={onClose} className="form-close-button" title="Fechar">
                        <X size={20} />
                    </button>
                </div>

                {isLoading ? (
                    <div className="table-loading-state">
                        <Loader2 size={24} className="animate-spin" />
                        <span>Carregando ficha...</span>
                    </div>
                ) : ficha ? (
                    <Fragment>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
                            <span style={{ fontSize: '0.8rem', color: '#475569' }}>{clienteNome}</span>
                            <span style={{ fontSize: '0.8rem', color: '#475569' }}>· {fmtData(ficha.createdAt)}</span>
                            {ficha.expirada && <span className="badge badge-red">Expirada</span>}
                            {servicoNome && <span className="badge badge-blue">{servicoNome}</span>}
                            {preenchidoPorNome && (
                                <span style={{ fontSize: '0.8rem', color: '#475569' }}>Preenchida por {preenchidoPorNome}</span>
                            )}
                        </div>

                        {(ficha.respostas || []).length === 0 ? (
                            <p className="text-muted text-center p-4">Nenhuma resposta registrada nesta ficha.</p>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                                {ficha.respostas.map((r, idx) => (
                                    <div key={idx} style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: '0.75rem' }}>
                                        <div style={{ fontSize: '0.875rem', fontWeight: 600 }}>{r.label}</div>
                                        <div style={{ fontSize: '0.95rem', fontWeight: 600, color: '#3b82f6' }}>
                                            {formatValorResposta(r.valor)}
                                        </div>
                                        {r.observacao && (
                                            <div style={{ fontSize: '0.8rem', color: '#475569', marginTop: 4 }}>
                                                Obs: {r.observacao}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}

                        {ficha.assinaturaCliente && (
                            <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: '0.75rem', marginTop: '0.75rem' }}>
                                <div style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.5rem' }}>Assinatura do Cliente</div>
                                <img
                                    src={ficha.assinaturaCliente}
                                    alt="Assinatura do cliente"
                                    style={{ width: '100%', maxHeight: 180, objectFit: 'contain', background: '#fff', borderRadius: 6 }}
                                />
                            </div>
                        )}
                    </Fragment>
                ) : (
                    <p className="text-muted">Ficha de anamnese não encontrada.</p>
                )}
            </div>
        </div>
    );
};

const AnamnesesHistoricoPage = () => {
    const { data: anamneses = [], isLoading, refetch } = useAnamnesesHistorico();
    const deleteAnamnese = useDeleteAnamnese();
    const [previewId, setPreviewId] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [continuarAlvo, setContinuarAlvo] = useState(null);
    const [selectedFicha, setSelectedFicha] = useState(null);
    const [assinarAlvo, setAssinarAlvo] = useState(null);
    const [linkAlvo, setLinkAlvo] = useState(null);

    const handleAdicionar = useCallback(() => { setContinuarAlvo(null); setShowForm(true); }, []);
    const handleCloseForm = useCallback(() => { setShowForm(false); setContinuarAlvo(null); }, []);
    const handleSaveSuccess = useCallback(() => {
        setShowForm(false);
        setContinuarAlvo(null);
        refetch();
    }, [refetch]);
    const handleContinuar = useCallback((ficha) => {
        setContinuarAlvo(ficha);
        setShowForm(true);
        setSelectedFicha(null);
    }, []);
    const handleExcluir = useCallback((ficha) => {
        if (window.confirm(`Excluir o rascunho de anamnese "${ficha.templateNome}"? Esta ação não pode ser desfeita.`)) {
            deleteAnamnese.mutate(ficha._id);
        }
        setSelectedFicha(null);
    }, [deleteAnamnese]);

    return (
        <Fragment>
            {previewId && <AnamnesePreviewModal anamneseId={previewId} onClose={() => setPreviewId(null)} />}
            {assinarAlvo && <AssinarAnamneseModal anamnese={assinarAlvo} onClose={() => setAssinarAlvo(null)} />}
            {linkAlvo && <GerarLinkAnamneseModal anamnese={linkAlvo} onClose={() => setLinkAlvo(null)} />}

            <div className="page-management-container">
                <PageHeader
                    icon={<ClipboardList />}
                    title="Fichas de Anamnese"
                    subtitle={!isLoading ? `${anamneses.length} ficha${anamneses.length !== 1 ? 's' : ''}` : null}
                    actions={showForm && (
                        <button onClick={handleCloseForm} className="action-button action-button-secondary">
                            <ArrowLeft size={18} className="icon" /> Voltar para Lista
                        </button>
                    )}
                />

                {showForm && (
                    <div className="content-panel">
                        <AnamneseForm onCloseForm={handleCloseForm} onSaveSuccess={handleSaveSuccess} fichaExistente={continuarAlvo} />
                    </div>
                )}
            </div>

            {!showForm && (
                <div className="table-container">
                    <div className="table-wrapper">
                        {isLoading ? (
                            <div className="table-loading-state">
                                <Loader2 size={24} className="animate-spin" />
                                <span>Carregando fichas de anamnese...</span>
                            </div>
                        ) : anamneses.length > 0 ? (
                            <table className="styled-table">
                                <thead>
                                    <tr>
                                        <th style={{ textAlign: 'left' }}>Cliente</th>
                                        <th>Template</th>
                                        <th>Serviço</th>
                                        <th>Preenchida em</th>
                                        <th>Andamento</th>
                                        <th>Validade</th>
                                        <th className="text-right">Ações</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {anamneses.map((ficha) => {
                                        const workflow = STATUS_WORKFLOW_LABEL[ficha.status] || STATUS_WORKFLOW_LABEL.filled;
                                        return (
                                            <tr key={ficha._id}>
                                                <td style={{ textAlign: 'left' }}>{getNome(ficha.cliente)}</td>
                                                <td>{ficha.templateNome || '—'}</td>
                                                <td>{ficha.servico && typeof ficha.servico === 'object' ? ficha.servico.nome : '—'}</td>
                                                <td style={{ fontSize: '0.8rem', color: '#475569', whiteSpace: 'nowrap' }}>
                                                    {fmtData(ficha.dataAssinatura || ficha.createdAt)}
                                                </td>
                                                <td>
                                                    <span className={`badge ${workflow.className}`}>{workflow.label}</span>
                                                </td>
                                                <td>
                                                    {ficha.expirada ? (
                                                        <span className="badge badge-red">Expirada</span>
                                                    ) : (
                                                        <span className="badge badge-green">Válida</span>
                                                    )}
                                                </td>
                                                <td className="text-right">
                                                    <button
                                                        onClick={() => setSelectedFicha(ficha)}
                                                        className="action-button-icon text-blue-600"
                                                        title="Ações"
                                                    >
                                                        <Eye size={18} className="icon" />
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        ) : (
                            <div className="table-empty-state">
                                <ClipboardList size={48} />
                                <h3>Nenhuma ficha de anamnese</h3>
                                <p>Fichas preenchidas para clientes aparecerão aqui.</p>
                            </div>
                        )}
                    </div>
                </div>
            )}

            <FabButton
                onClick={handleAdicionar}
                title="Nova Ficha"
                visible={!showForm}
                icon={<ClipboardList size={26} className="icon" />}
            />

            <ActionsDrawer
                open={!!selectedFicha}
                onClose={() => setSelectedFicha(null)}
                title={selectedFicha?.templateNome || 'Ficha de Anamnese'}
                subtitle={getNome(selectedFicha?.cliente)}
                actions={selectedFicha ? [
                    {
                        key: 'visualizar',
                        label: 'Visualizar',
                        icon: <Eye size={20} className="icon" />,
                        onClick: () => { setPreviewId(selectedFicha._id); setSelectedFicha(null); },
                    },
                    ...(selectedFicha.status === 'draft' ? [
                        {
                            key: 'continuar',
                            label: 'Continuar Preenchimento',
                            icon: <FileEdit size={20} className="icon" />,
                            onClick: () => handleContinuar(selectedFicha),
                        },
                        {
                            key: 'excluir',
                            label: 'Excluir Rascunho',
                            icon: <Trash2 size={20} className="icon" />,
                            onClick: () => handleExcluir(selectedFicha),
                        },
                    ] : []),
                    ...(selectedFicha.status === 'awaiting_signature' ? [
                        {
                            key: 'assinar',
                            label: 'Assinar Agora',
                            icon: <PenLine size={20} className="icon" />,
                            onClick: () => { setAssinarAlvo(selectedFicha); setSelectedFicha(null); },
                        },
                        {
                            key: 'gerar-link',
                            label: 'Gerar Link de Assinatura',
                            icon: <Link2 size={20} className="icon" />,
                            onClick: () => { setLinkAlvo(selectedFicha); setSelectedFicha(null); },
                        },
                    ] : []),
                ] : []}
            />
        </Fragment>
    );
};

export default AnamnesesHistoricoPage;
